import { Mail, Lock, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { AuthInput } from './AuthInput';
import { AuthLayout } from './AuthLayout';
import { signInWithEmailAndPassword, signOut, linkWithCredential, EmailAuthProvider } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

export const Login = ({ isLight, onNavigate, onLoginSuccess, db, appId, auth }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError('');

    try {
      const credential = EmailAuthProvider.credential(email, password);
      let user;

      // 1. Identify if we are "promoting" a guest or starting fresh
      if (auth.currentUser?.isAnonymous) {
        try {
          const userCredential = await linkWithCredential(auth.currentUser, credential);
          user = userCredential.user;
          console.log("Guest session promoted to existing account.");
        } catch (linkError) {
          // We handle the merge failure silently
          if (linkError.code === 'auth/credential-already-in-use' || linkError.code === 'auth/email-already-in-use') {
            try {
              const userCredential = await signInWithEmailAndPassword(auth, email, password);
              user = userCredential.user;
            } catch (loginError) {
              // If the password is wrong, we throw a GENERIC error.
              // We do NOT say "Wrong Password". 
              throw new Error('auth/invalid-credential'); 
            }
          } else {
            throw linkError; 
          }
        }
      } else {
        // Normal: Just sign in
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        user = userCredential.user;
      }

      // 2. CHECK FOR EMAIL VERIFICATION (Standard flow)
      if (!user.emailVerified) {
        await signOut(auth);
        setError('Check your inbox and verify your email to log in.');
        setLoading(false);
        return;
      }

      // 3. Sync with Firestore
      const userDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'users', user.uid);
      const userSnapshot = await getDoc(userDocRef);

      let userData;

      if (userSnapshot.exists()) {
        // If user exists, get their data
        userData = userSnapshot.data();
        
        // If they were anonymous, we might want to ensure 'isAnonymous' flag is now false in DB
        if (userData.isAnonymous) {
          await updateDoc(userDocRef, { isAnonymous: false });
          userData.isAnonymous = false;
        }
        
      } else {
        // 4. ONBOARDING: If user is new, create their profile in Firestore
        userData = {
          displayName: user.displayName,
          email: user.email,
          uid: user.uid,
          emailVerified: user.emailVerified,
          createdAt: new Date().toISOString(),
          tier: {
            subscriptionId: null,
            type: "free",
            tierRenewalDates: [new Date().toISOString()],
            tierExpiresOn: null
          },
          usage: {
            creditsUsed: 0,                                   // Per Day Usage
            creditsLimit: 10,                                 // Per Day Limit
            lastReset: new Date().toISOString(),
            history: null
          }
        };
        await setDoc(userDocRef, userData);
      }

      // 5. Pass the complete user data to the parent state and navigate
      onLoginSuccess({ ...userData, uid: user.uid });
      onNavigate('/scanner');

    } catch (err) {
      console.error(err.code, err.message);
      let newError = 'Authentication failed. Please try again later.';
      if (err.code === 'auth/email-already-in-use') {
        newError = 'This email is already registered. Please sign in normally.';
      }
      else if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        newError = 'Invalid email or password.';
      } else if (err.code === 'auth/user-disabled') {
        newError = 'This account has been disabled.';
      }
      setError(newError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout 
      title="Welcome Back" 
      subtitle="Sign in to continue your secure session"
      isLight={isLight}
      modeText="Need an account?"
      modeAction="Sign up here"
      onToggleMode={() => onNavigate('/register')}
    >
      <form onSubmit={handleLogin}>
        <AuthInput label="Email Address" type="email" icon={Mail} placeholder="name@email.com" value={email} onChange={setEmail} isLight={isLight} />
        <AuthInput label="Password" type="password" icon={Lock} placeholder="Enter your password" value={password} onChange={setPassword} isLight={isLight} />
        
        <div className="text-right -mt-2 mb-4">
          <button
            type="button" // Important to prevent form submission
            onClick={() => onNavigate('/forgot-password')}
            className="text-xs text-slate-500 hover:text-cyan-500 transition-colors underline-offset-4 hover:underline"
          >
            Forgot Password?
          </button>
        </div>

        {error && <p className="text-red-500 text-[10px] mb-4 uppercase tracking-wider">{error}</p>}

        <button 
          disabled={loading}
          type="submit"
          className={`w-full mt-4 font-black py-5 rounded uppercase text-xs tracking-[0.2em] flex items-center justify-center gap-2 transition-all ${
            loading ? 'opacity-50 cursor-not-allowed' : ''
          } ${isLight ? 'bg-slate-900 text-white hover:bg-slate-800' : 'bg-white text-black hover:bg-slate-200'}`}
        >
          {loading ? 'Authenticating...' : 'Sign In'} <ArrowRight size={14} />
        </button>
      </form>
    </AuthLayout>
  );
};
